import { create } from 'zustand'

/**
 * Live reactions, per participant.
 *
 * Was a single `currentReaction` string handed to every tile, so one person's
 * 👍 appeared in the corner of everybody's tile at once — and a stage-level
 * burst played from a fixed spot on the right edge, which read as the reaction
 * coming out of whichever tile happened to sit there.
 *
 * A reaction belongs to whoever sent it, so it is keyed by peerId and rendered
 * only on their tile. Each expires on its own timer: two people reacting a
 * second apart must not cut each other short.
 */

type MeetingReactionsState = {
  /** peerId → the emoji they are showing right now. */
  reactionsByPeer: Record<string, string>
  /** Show `emoji` on `peerId`'s tile for a few seconds. */
  triggerReaction: (emoji: string, peerId: string) => void
  /** Drop a peer's reaction early (they left, or the meeting ended). */
  clearReaction: (peerId: string) => void
  clearAll: () => void
}

const REACTION_DURATION_MS = 4000

/** Per-peer timers, so a second reaction replaces the first cleanly. */
const timers = new Map<string, number>()

export const useMeetingReactionsStore = create<MeetingReactionsState>((set) => ({
  reactionsByPeer: {},

  triggerReaction: (emoji, peerId) => {
    if (!emoji || !peerId) return

    set((prev) => ({ reactionsByPeer: { ...prev.reactionsByPeer, [peerId]: emoji } }))

    const existing = timers.get(peerId)
    if (existing !== undefined) window.clearTimeout(existing)

    if (typeof window !== 'undefined') {
      const id = window.setTimeout(() => {
        timers.delete(peerId)
        set((prev) => {
          const { [peerId]: _gone, ...rest } = prev.reactionsByPeer
          return { reactionsByPeer: rest }
        })
      }, REACTION_DURATION_MS)
      timers.set(peerId, id)
    }
  },

  clearReaction: (peerId) =>
    set((prev) => {
      const timer = timers.get(peerId)
      if (timer !== undefined) {
        window.clearTimeout(timer)
        timers.delete(peerId)
      }
      const { [peerId]: _gone, ...rest } = prev.reactionsByPeer
      return { reactionsByPeer: rest }
    }),

  clearAll: () => {
    for (const timer of timers.values()) window.clearTimeout(timer)
    timers.clear()
    set({ reactionsByPeer: {} })
  },
}))
